import React, { useState, useEffect } from 'react';
import { Card, Select, DatePicker, Space, Typography, Spin, Alert, Row, Col, Statistic, Button } from 'antd';
import { LineChartOutlined, CalendarOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import dailyPriceService from '../services/dailyPriceService';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const DailyPriceChart = ({ stockSymbols = [], stockList = [], holdingsData = [], broker = null, title = '종가 추이', hideAmounts = false }) => {
  const [selectedStock, setSelectedStock] = useState(null);
  const [dateRange, setDateRange] = useState([
    moment().subtract(30, 'day'),
    moment()
  ]);
  const [priceData, setPriceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState({ categories: [], series: [] });
  const [priceStats, setPriceStats] = useState(null);

  // 종목 선택 시 데이터 로드
  useEffect(() => {
    if (selectedStock && dateRange && dateRange.length === 2) {
      loadPriceData();
    }
  }, [selectedStock, dateRange]);

  // 첫 번째 종목 자동 선택
  useEffect(() => {
    if (stockSymbols.length > 0 && !selectedStock) {
      setSelectedStock(stockSymbols[0]);
    }
  }, [stockSymbols]);

  const loadPriceData = async () => {
    if (!selectedStock || !dateRange || dateRange.length !== 2) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const dateFrom = dateRange[0].format('YYYY-MM-DD');
      const dateTo = dateRange[1].format('YYYY-MM-DD');

      // 날짜 범위를 일수로 계산
      const daysDiff = moment(dateTo).diff(moment(dateFrom), 'days') + 1;
      
      // 최적화된 다중 종목 API 사용 (단일 종목이지만 캐싱 혜택)
      const holdingsPrices = await dailyPriceService.getHoldingsRecentPrices(
        [selectedStock],
        daysDiff,
        broker
      );

      // 결과 추출
      const stockData = holdingsPrices[selectedStock];
      let data = [];
      
      if (stockData && stockData.success && stockData.data) {
        // 날짜 범위 필터링 (정확한 범위만)
        data = stockData.data.filter(item => {
          const itemDate = moment(item.date);
          return itemDate.isSameOrAfter(dateFrom) && itemDate.isSameOrBefore(dateTo);
        });
      }

      setPriceData(data);

      // 차트 데이터 포맷팅
      const formattedData = dailyPriceService.formatPriceDataForChart(data);
      setChartData(formattedData);

      // 통계 계산
      calculatePriceStats(data);

    } catch (error) {
      console.error('❌ 종가 데이터 로드 실패:', error);
      setError(error.message || '종가 데이터를 불러올 수 없습니다.');
      setPriceData([]);
      setChartData({ categories: [], series: [] });
      setPriceStats(null);
    } finally {
      setLoading(false);
    }
  };

  // 종목 심볼로 종목명 찾기
  const getStockName = (symbol) => {
    const stock = stockList.find(s => s.symbol === symbol);
    return stock ? stock.name : symbol;
  };

  // 선택된 종목의 표시명 (종목명(코드))
  const getSelectedStockDisplayName = () => {
    if (!selectedStock) return '';
    const stockName = getStockName(selectedStock);
    return stockName !== selectedStock ? `${stockName}(${selectedStock})` : selectedStock;
  };

  // 선택된 종목의 보유 정보 찾기
  const getSelectedStockHolding = () => {
    if (!selectedStock || !holdingsData || holdingsData.length === 0) {
      return null;
    }
    return holdingsData.find(holding => holding.symbol === selectedStock) || null;
  };

  const calculatePriceStats = (data) => {
    if (!data || data.length === 0) {
      setPriceStats(null);
      return;
    }

    // 날짜순 정렬
    const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    const prices = sortedData.map(item => parseFloat(item.closing_price)).filter(p => !isNaN(p));
    if (prices.length === 0) {
      setPriceStats(null);
      return;
    }
    const firstPrice = prices[0];
    const lastPrice = prices[prices.length - 1];
    const maxPrice = Math.max(...prices);
    const minPrice = Math.min(...prices);
    
    const totalChange = lastPrice - firstPrice;
    const totalChangePercent = firstPrice !== 0 ? (totalChange / firstPrice) * 100 : 0;

    setPriceStats({
      firstPrice,
      lastPrice,
      maxPrice,
      minPrice,
      totalChange,
      totalChangePercent,
      dataCount: data.length
    });
  };

  const getChartOptions = () => {
    const holdingInfo = getSelectedStockHolding();
    const avgBuyPrice = holdingInfo ? parseFloat(holdingInfo.avg_buy_price) || 0 : 0;

    // yaxis min/max 계산 (NaN 방지)
    let yAxisExtra = {};
    if (avgBuyPrice > 0 && priceStats && !isNaN(priceStats.minPrice) && !isNaN(priceStats.maxPrice)) {
      const minPrice = Math.min(priceStats.minPrice, avgBuyPrice);
      const maxPrice = Math.max(priceStats.maxPrice, avgBuyPrice);
      const margin = (maxPrice - minPrice) * 0.05;
      yAxisExtra = {
        min: Math.max(0, minPrice - margin),
        max: maxPrice + margin
      };
    }

    return {
      chart: {
        type: 'line',
        height: 400,
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true
          }
        },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true
        }
      },
      annotations: avgBuyPrice > 0 ? {
        yaxis: [
          {
            y: avgBuyPrice,
            borderColor: '#ff9500',
            borderWidth: 2,
            strokeDashArray: 5,
            label: {
              borderColor: '#ff9500',
              style: {
                color: '#fff',
                background: '#ff9500',
                fontSize: '12px'
              },
              text: `평균단가: ₩${avgBuyPrice.toLocaleString()}`
            }
          }
        ]
      } : {},
      title: {
        text: `${getSelectedStockDisplayName()} 종가 추이`,
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'bold'
        }
      },
      xaxis: {
        // plain 배열 데이터를 사용하므로 categories 방식 사용
        // type: 'datetime'을 제거 - categories 문자열과 충돌하여 잘못된 SVG path 좌표 생성
        categories: chartData.categories,
        tickAmount: Math.min(chartData.categories.length, 10),
        labels: {
          rotate: -45,
          formatter: function (val) {
            // "YYYY-MM-DD" 형식에서 "MM/DD"만 표시
            if (val && typeof val === 'string' && val.length >= 10) {
              return val.substring(5).replace('-', '/');
            }
            return val;
          }
        }
      },
      yaxis: [
        {
          title: {
            text: '종가 (원)'
          },
          labels: {
            formatter: function (value) {
              return value ? `₩${value.toLocaleString()}` : '';
            }
          },
          ...yAxisExtra
        },
        {
          opposite: true,
          title: {
            text: '거래량'
          },
          labels: {
            formatter: function (value) {
              return value ? value.toLocaleString() : '';
            }
          }
        }
      ],
      stroke: {
        width: [3, 1],
        curve: 'smooth'
      },
      colors: ['#1890ff', '#52c41a'],
      fill: {
        type: ['solid', 'gradient'],
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.25,
          gradientToColors: undefined,
          inverseColors: true,
          opacityFrom: 0.85,
          opacityTo: 0.05,
          stops: [50, 0, 100]
        }
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'left'
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: [
          {
            formatter: function (value) {
              return value ? `₩${value.toLocaleString()}` : '';
            }
          },
          {
            formatter: function (value) {
              return value ? value.toLocaleString() : '';
            }
          }
        ]
      },
      grid: {
        borderColor: '#f1f1f1'
      }
    };
  };

  const handleStockChange = (value) => {
    setSelectedStock(value);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  // 프리셋 날짜 범위 설정 함수들
  const setThisMonth = () => {
    setDateRange([moment().startOf('month'), moment()]);
  };

  const setLastMonth = () => {
    setDateRange([moment().subtract(1, 'month'), moment()]);
  };

  const setLastThreeMonths = () => {
    setDateRange([moment().subtract(3, 'months'), moment()]);
  };

  const setLastSixMonths = () => {
    setDateRange([moment().subtract(6, 'months'), moment()]);
  };

  const setLastYear = () => {
    setDateRange([moment().subtract(1, 'year'), moment()]);
  };

  return (
    <Card
      title={
        <Space>
          <LineChartOutlined />
          <span>{title}</span>
        </Space>
      }
      extra={
        <Space>
          <Select
            value={selectedStock}
            onChange={handleStockChange}
            style={{ width: 200 }}
            placeholder="종목 선택"
            disabled={loading || stockSymbols.length === 0}
          >
            {stockSymbols.map(symbol => {
              const stockName = getStockName(symbol);
              const displayName = stockName !== symbol ? `${stockName}(${symbol})` : symbol;
              return (
                <Option key={symbol} value={symbol}>
                  {displayName}
                </Option>
              );
            })}
          </Select>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            disabled={loading}
            allowClear={false}
          />
        </Space>
      }
    >
      {error && (
        <Alert
          message="데이터 로드 실패"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {stockSymbols.length === 0 && (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <LineChartOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
          <Title level={4} type="secondary">보유 종목이 없습니다</Title>
          <Text type="secondary">종목을 보유하면 종가 추이를 확인할 수 있습니다.</Text>
        </div>
      )}

      {stockSymbols.length > 0 && (
        <>
          {/* 날짜 범위 프리셋 버튼 */}
          <div style={{ marginBottom: 16, textAlign: 'center' }}>
            <Space wrap>
              <Button size="small" onClick={setThisMonth} disabled={loading}>이번달</Button>
              <Button size="small" onClick={setLastMonth} disabled={loading}>최근 한달</Button>
              <Button size="small" onClick={setLastThreeMonths} disabled={loading}>최근 석달</Button>
              <Button size="small" onClick={setLastSixMonths} disabled={loading}>최근 여섯달</Button>
              <Button size="small" onClick={setLastYear} disabled={loading}>최근 1년</Button>
            </Space>
          </div>

          {/* 가격 통계 */}
          {priceStats && (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={12} sm={6}>
                <Statistic
                  title="시작가"
                  value={priceStats.firstPrice}
                  precision={0}
                  prefix="₩"
                  valueStyle={{ fontSize: '14px' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="종료가"
                  value={priceStats.lastPrice}
                  precision={0}
                  prefix="₩"
                  valueStyle={{ fontSize: '14px' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="최고가"
                  value={priceStats.maxPrice}
                  precision={0}
                  prefix="₩"
                  valueStyle={{ fontSize: '14px', color: '#ff4d4f' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="최저가"
                  value={priceStats.minPrice}
                  precision={0}
                  prefix="₩"
                  valueStyle={{ fontSize: '14px', color: '#52c41a' }}
                />
              </Col>
            </Row>
          )}

          {/* 변화율 통계 */}
          {priceStats && (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12}>
                <Statistic
                  title="기간 변화"
                  value={Math.abs(priceStats.totalChange)}
                  precision={0}
                  prefix={priceStats.totalChange >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  suffix="₩"
                  valueStyle={{
                    color: priceStats.totalChange >= 0 ? '#3f8600' : '#cf1322',
                    fontSize: '16px'
                  }}
                />
              </Col>
              <Col xs={24} sm={12}>
                <Statistic
                  title="변화율"
                  value={Math.abs(priceStats.totalChangePercent)}
                  precision={2}
                  prefix={priceStats.totalChangePercent >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  suffix="%"
                  valueStyle={{
                    color: priceStats.totalChangePercent >= 0 ? '#3f8600' : '#cf1322',
                    fontSize: '16px'
                  }}
                />
              </Col>
            </Row>
          )}

          {/* 보유 주식 정보 */}
          {(() => {
            const holdingInfo = getSelectedStockHolding();
            return holdingInfo && (
              <Row gutter={16} style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f6ffed', borderRadius: '8px', border: '1px solid #b7eb8f' }}>
                <Col span={24} style={{ marginBottom: '8px' }}>
                  <Text strong style={{ color: '#52c41a' }}>보유 정보</Text>
                </Col>
                <Col xs={12} sm={6}>
                  {hideAmounts ? (
                    <div>
                      <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>평균단가</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1890ff' }}>******</div>
                    </div>
                  ) : (
                    <Statistic
                      title="평균단가"
                      value={holdingInfo.avg_buy_price}
                      precision={0}
                      prefix="₩"
                      valueStyle={{ fontSize: '14px', color: '#1890ff' }}
                    />
                  )}
                </Col>
                <Col xs={12} sm={6}>
                  {hideAmounts ? (
                    <div>
                      <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>현재가</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1890ff' }}>******</div>
                    </div>
                  ) : (
                    <Statistic
                      title="현재가"
                      value={holdingInfo.current_price}
                      precision={0}
                      prefix="₩"
                      valueStyle={{ fontSize: '14px', color: '#1890ff' }}
                    />
                  )}
                </Col>
                <Col xs={12} sm={6}>
                  {hideAmounts ? (
                    <div>
                      <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>평가금액</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#722ed1' }}>******</div>
                    </div>
                  ) : (
                    <Statistic
                      title="평가금액"
                      value={holdingInfo.evaluation_amount}
                      precision={0}
                      prefix="₩"
                      valueStyle={{ fontSize: '14px', color: '#722ed1' }}
                    />
                  )}
                </Col>
                <Col xs={12} sm={6}>
                  {hideAmounts ? (
                    <div>
                      <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>손익</div>
                      <div style={{ display: 'flex', alignItems: 'center', color: (holdingInfo.profit_loss || 0) >= 0 ? '#3f8600' : '#cf1322' }}>
                        {(holdingInfo.profit_loss || 0) >= 0 ? <ArrowUpOutlined style={{ marginRight: '4px' }} /> : <ArrowDownOutlined style={{ marginRight: '4px' }} />}
                        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>******</span>
                      </div>
                    </div>
                  ) : (
                    <Statistic
                      title="손익"
                      value={Math.abs(holdingInfo.profit_loss || 0)}
                      precision={0}
                      prefix={
                        <span>
                          {(holdingInfo.profit_loss || 0) >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                          ₩
                        </span>
                      }
                      suffix={
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          ({(holdingInfo.profit_loss_percent || 0).toFixed(2)}%)
                        </Text>
                      }
                      valueStyle={{
                        color: (holdingInfo.profit_loss || 0) >= 0 ? '#3f8600' : '#cf1322',
                        fontSize: '14px'
                      }}
                    />
                  )}
                </Col>
              </Row>
            );
          })()}

          {/* 차트 */}
          <Spin spinning={loading}>
            {chartData.series.length > 0 ? (
              <ReactApexChart
                options={getChartOptions()}
                series={chartData.series}
                type="line"
                height={400}
              />
            ) : (
              !loading && (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <CalendarOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                  <Title level={4} type="secondary">데이터가 없습니다</Title>
                  <Text type="secondary">선택한 기간에 종가 데이터가 없습니다.</Text>
                </div>
              )
            )}
          </Spin>
        </>
      )}
    </Card>
  );
};

export default DailyPriceChart;
